import { Fragment, useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import type {
  FeatureIcon,
  MarqueeItemBlock,
  MarqueeSection,
} from '@/features/storefront/sections/schema';
import { shownBlocks } from '@/features/storefront/sections/schema';
import { cn } from '@/lib/cn';

/**
 * A thin band of short benefits scrolling sideways without end.
 *
 * **One loop, read once.** The items are drawn as many times as it takes to
 * be wider than a wide screen, and then that whole run twice: the track slides
 * by exactly half its width and starts again, so the seam never shows. Only
 * the very first run is offered to screen readers - every copy after it is
 * `aria-hidden` - so the band is heard as one list, not a dozen.
 *
 * **Still for those who ask.** A shopper whose system asks for reduced motion
 * gets the items once, standing still in a centred row that wraps; the CSS
 * says the same thing for a page drawn before the script runs. The band never
 * widens the page: it clips what runs past its edges.
 */

export interface MarqueeViewProps {
  section: MarqueeSection;
  includeHidden?: boolean;
  /** A feature icon, drawn the way the renderer draws one. */
  icon: (name: FeatureIcon) => ReactNode;
  /** An item's words in the first (spoken) run, typeable in the customiser. */
  text?: (block: MarqueeItemBlock) => ReactNode;
  /** Wraps an item in the first run, so the customiser can select the block. */
  wrap?: (block: MarqueeItemBlock, rendered: ReactNode) => ReactNode;
  /** Shown in the customiser while no item has words. */
  emptyHint?: ReactNode;
}

/** The items a shopper sees: shown ones with words. */
export function marqueeItems(section: MarqueeSection, includeHidden = false): MarqueeItemBlock[] {
  return shownBlocks(section.blocks, includeHidden).filter((block) => block.text.trim());
}

/** How far the band travels each second, in pixels. */
const SPEED_PX: Record<MarqueeSection['speed'], number> = { slow: 30, normal: 55, fast: 95 };

const SIZE_CLASS: Record<MarqueeSection['size'], string> = {
  sm: 'text-xs gap-x-6 [--marquee-gap:1.5rem]',
  md: 'text-sm gap-x-8 [--marquee-gap:2rem]',
  lg: 'text-lg gap-x-10 [--marquee-gap:2.5rem]',
};

/** Roughly how wide one character is drawn at each size, for the loop's length. */
const CHAR_PX: Record<MarqueeSection['size'], number> = { sm: 7, md: 8.5, lg: 11 };

const SEPARATORS: Record<MarqueeSection['separator'], string> = { dot: '•', star: '★', none: '' };

/** Wider than any screen the band is likely to stand across. */
const RUN_PX = 2400;

/** How many times the items repeat in one half of the loop, and how long that half takes. */
export function marqueeTiming(
  section: MarqueeSection,
  items: MarqueeItemBlock[],
): { repeats: number; seconds: number } {
  const perItem = 48 + (section.separator === 'none' ? 0 : 24);
  const width = items.reduce(
    (total, block) => total + block.text.trim().length * CHAR_PX[section.size] + perItem,
    0,
  );
  const repeats = Math.min(12, Math.max(1, Math.ceil(RUN_PX / Math.max(width, 1))));
  const seconds = Math.max(8, Math.round((width * repeats) / SPEED_PX[section.speed]));
  return { repeats, seconds };
}

/** Whether the shopper's system asks for less motion. */
function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const onChange = () => setReduced(list.matches);
    list.addEventListener?.('change', onChange);
    return () => list.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

const MARQUEE_CSS = `
@keyframes storefront-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes storefront-marquee-rtl { from { transform: translateX(0); } to { transform: translateX(50%); } }
[data-marquee-track] {
  animation: storefront-marquee var(--marquee-duration, 30s) linear infinite;
  animation-direction: var(--marquee-direction, normal);
}
[dir='rtl'] [data-marquee-track] { animation-name: storefront-marquee-rtl; }
[data-marquee-pause]:hover [data-marquee-track] { animation-play-state: paused; }
@media (prefers-reduced-motion: reduce) { [data-marquee-track] { animation: none; } }
`;

export function MarqueeView({
  section,
  includeHidden = false,
  icon,
  text,
  wrap,
  emptyHint,
}: MarqueeViewProps) {
  const reduced = usePrefersReducedMotion();
  const items = marqueeItems(section, includeHidden);
  if (items.length === 0) return <>{emptyHint ?? null}</>;

  const separator = SEPARATORS[section.separator];
  const rowClass = cn(
    'flex items-center font-semibold',
    SIZE_CLASS[section.size],
    section.uppercase && 'uppercase tracking-wider',
  );

  function item(block: MarqueeItemBlock, spoken: boolean) {
    return (
      <span className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap" data-marquee-item>
        {block.icon ? <span className="inline-flex shrink-0">{icon(block.icon)}</span> : null}
        <span>{spoken && text ? text(block) : block.text}</span>
      </span>
    );
  }

  function separatorMark(key: string) {
    return separator ? (
      <span key={key} aria-hidden className="shrink-0 opacity-60" data-marquee-separator>
        {separator}
      </span>
    ) : null;
  }

  if (reduced) {
    return (
      <div className="px-5 sm:px-8" data-marquee="static">
        <div className={cn(rowClass, 'flex-wrap justify-center gap-y-2 text-center')}>
          {items.map((block, index) => (
            <Fragment key={block.id}>
              {index > 0 ? separatorMark(`sep-${block.id}`) : null}
              {wrap ? wrap(block, item(block, true)) : item(block, true)}
            </Fragment>
          ))}
        </div>
      </div>
    );
  }

  const { repeats, seconds } = marqueeTiming(section, items);
  const runs = Array.from({ length: repeats * 2 }, (_, index) => index);
  const style = {
    '--marquee-duration': `${seconds}s`,
    '--marquee-direction': section.direction === 'right' ? 'reverse' : 'normal',
  } as CSSProperties;

  return (
    <div
      className="overflow-hidden"
      data-marquee="moving"
      data-marquee-pause={section.pause_on_hover ? '' : undefined}
    >
      <style>{MARQUEE_CSS}</style>
      <div className="flex w-max" data-marquee-track style={style}>
        {runs.map((run) => (
          <div
            key={run}
            // Every run after the first is the same words again, for the eye only.
            aria-hidden={run > 0 ? true : undefined}
            className={cn(rowClass, 'shrink-0 pe-[var(--marquee-gap)]')}
            data-marquee-run={run}
          >
            {items.map((block) => {
              const rendered = item(block, run === 0);
              return (
                <Fragment key={block.id}>
                  {run === 0 && wrap ? wrap(block, rendered) : rendered}
                  {separatorMark(`sep-${block.id}`)}
                </Fragment>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
